import { styled } from '@mui/material';
import { useDialog } from 'contexts/dialog';
import { useViewport } from 'contexts/viewport';
import { omit } from 'lodash';
import { memo } from 'react';
import { Button, Icon, IconButton, Paper, RichTextArea, Stack, Tag, Text, TextInput, Tooltip } from 'shell/components';
import { CONTENT_BANK_RESOURCES_MB_LIMIT, DESCRIPTION_WORD_COUNT_LIMIT } from 'utils/constants';
import { bytesToMB, getHTMLWordCount, getTooltipComponent } from 'utils/helper';

const GeneralSection = memo(
    ({
        isEditable,
        form,
        setForm,
        errors,
        setErrors,
        setSelectRubricDialog,
        setEditRubricDialog,
        setEditBaselineDialog,
        setUploadContentBankResourcesDialog,
        setEditContentBankResourcesDialog,
    }) => {
        const { setDialog, closeDialog } = useDialog();
        const { device } = useViewport();

        const updateForm = (key, value) => {
            setForm((current) => ({
                ...current,
                [key]: value,
            }));
        };

        const handleClickEditRubric = () => {
            if (!form.baseline) return setEditRubricDialog({ open: true });
            setDialog({
                open: true,
                message: 'Rubric Modifications Will Reset the Baseline',
                description: 'Modifying the rubric will require re-grading the baseline. Do you want to continue?',
                note: '',
                variant: 'destructive',
                actionButtonText: 'Continue',
                cancelButtonText: 'Cancel',
                onAction: () => {
                    setEditRubricDialog({ open: true });
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        };

        const handleClickDeleteRubric = () => {
            setDialog({
                open: true,
                message: 'Delete Rubric?',
                description: 'Are you sure you want to delete this rubric?',
                note: '',
                variant: 'destructive',
                actionButtonText: 'Delete',
                cancelButtonText: 'Cancel',
                onAction: () => {
                    setForm((current) => ({
                        ...current,
                        rubric: null,
                        baseline: null,
                        feedbackSettings: { ...current.feedbackSettings, structure: 'system' },
                        gradingSettings: null,
                    }));
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        };

        const handleClickEditBaseline = () => {
            setEditBaselineDialog({ open: true });
        };

        const handleClickDeleteBaseline = () => {
            setDialog({
                open: true,
                message: 'Delete Baseline?',
                description: 'Are you sure you want to delete this baseline?',
                note: '',
                variant: 'destructive',
                actionButtonText: 'Delete',
                cancelButtonText: 'Cancel',
                onAction: () => {
                    setForm((current) => ({ ...current, baseline: null }));
                    setErrors((current) => omit(current, ['baseline']));
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        };

        return (
            <RootStack sx={{ width: '100%' }} device={device} spacing={1}>
                <Text variant={'p1'}>General Assignment Details</Text>
                <Paper variant={'outlined'}>
                    <Stack spacing={4}>
                        <CustomTextInput
                            error={errors.title}
                            placeholder={'Enter assignment title'}
                            value={form.title}
                            onChange={(e) => updateForm('title', e.target.value)}
                        />
                        <RichTextArea
                            disabled={!isEditable}
                            error={
                                errors.description || getHTMLWordCount(form.description) > DESCRIPTION_WORD_COUNT_LIMIT
                            }
                            label={'Assignment Description'}
                            helperText={`${getHTMLWordCount(form.description)}/${DESCRIPTION_WORD_COUNT_LIMIT} words`}
                            helperTextPosition={'end'}
                            placeholder={'Enter assignment details'}
                            required
                            value={form.description}
                            onChange={(value) => updateForm('description', value)}
                        />
                        <Stack spacing={2}>
                            <Text>
                                Assignment Rubric{' '}
                                <Text color={'error'} component={'span'}>
                                    *
                                </Text>
                            </Text>
                            {form.rubric ? (
                                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                    <Text color={'light'}>{form.rubric.title}</Text>
                                    <Stack direction={'row'} spacing={2}>
                                        <IconButton
                                            aria-label={'edit rubric'}
                                            disabled={!isEditable}
                                            kind={'edit'}
                                            size={'small'}
                                            variant={'square'}
                                            onClick={handleClickEditRubric}
                                        />
                                        <IconButton
                                            aria-label={'delete rubric'}
                                            disabled={!isEditable}
                                            kind={'delete'}
                                            size={'small'}
                                            variant={'square'}
                                            onClick={handleClickDeleteRubric}
                                        />
                                    </Stack>
                                </Stack>
                            ) : (
                                <CustomButton
                                    color={errors.rubric ? 'destructive' : 'primary'}
                                    startIcon={<Icon kind={'add'} />}
                                    variant={'borderless'}
                                    onClick={() => setSelectRubricDialog({ open: true })}
                                >
                                    Add a Rubric
                                </CustomButton>
                            )}
                        </Stack>
                        {form.baseline && (
                            <Stack spacing={2}>
                                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                    <Text>Baseline</Text>
                                    <Tooltip
                                        content={getTooltipComponent(
                                            'Creating a Baseline',
                                            'For optimal AI feedback and grading outcomes, we recommend uploading and grading a previous assignment. This will help align the AI with your assessment practices.'
                                        )}
                                        type={'info'}
                                    />
                                    <Tag type={'general'} value={'recommended'} />
                                </Stack>
                                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                    <Text color={errors.baseline ? 'error' : 'light'}>
                                        {errors.baseline
                                            ? `Assignment Baseline ${errors.baseline}`
                                            : 'Assignment Baseline'}
                                    </Text>
                                    <Stack direction={'row'} spacing={2}>
                                        <IconButton
                                            aria-label={'edit baseline'}
                                            disabled={!isEditable}
                                            kind={'edit'}
                                            size={'small'}
                                            variant={'square'}
                                            onClick={handleClickEditBaseline}
                                        />
                                        <IconButton
                                            aria-label={'delete baseline'}
                                            disabled={!isEditable}
                                            kind={'delete'}
                                            size={'small'}
                                            variant={'square'}
                                            onClick={handleClickDeleteBaseline}
                                        />
                                    </Stack>
                                </Stack>
                            </Stack>
                        )}
                        <Stack spacing={2}>
                            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                <Text>Content Bank</Text>
                                <Tooltip
                                    content={
                                        'Adding files to your Content Bank allows our AI to retrieve relevant context from these documents, enabling it to generate more personalized and accurate feedback and grading suggestions.'
                                    }
                                    type={'info'}
                                />
                                <Text color={'light'}>
                                    [
                                    {bytesToMB(
                                        form.contentBankResources
                                            .filter(({ active }) => active)
                                            .reduce((acc, cur) => acc + cur.data.size, 0)
                                    )}
                                    /{CONTENT_BANK_RESOURCES_MB_LIMIT}MB used]
                                </Text>
                            </Stack>
                            {form.contentBankResources.filter(({ active }) => active).length ? (
                                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                                    <Text color={'light'}>
                                        {form.contentBankResources.filter(({ active }) => active).length} file(s) added
                                    </Text>
                                    <Stack direction={'row'} spacing={2}>
                                        <IconButton
                                            aria-label={'edit content bank'}
                                            disabled={!isEditable}
                                            kind={'edit'}
                                            size={'small'}
                                            variant={'square'}
                                            onClick={() => setEditContentBankResourcesDialog({ open: true })}
                                        />
                                    </Stack>
                                </Stack>
                            ) : null}
                            <CustomButton
                                disabled={!isEditable}
                                startIcon={<Icon kind={'add'} />}
                                variant={'borderless'}
                                onClick={() => setUploadContentBankResourcesDialog({ open: true })}
                            >
                                Add file
                            </CustomButton>
                        </Stack>
                    </Stack>
                </Paper>
            </RootStack>
        );
    }
);

const RootStack = styled(Stack)(() => {
    return {
        width: '100%',
    };
});

const CustomTextInput = styled(TextInput)(({ theme }) => {
    return {
        margin: theme.spacing(-2, -3),
        width: `calc(100% + ${theme.spacing(6)})`,
        '&& input': {
            fontSize: `${theme.typography.sh1.fontSize.large}rem`,
            fontWeight: theme.typography.sh1.fontWeight.semibold,
        },
        '&& fieldset': {
            borderColor: 'transparent',
        },
    };
});

const CustomButton = styled(Button)(() => {
    return {
        width: 'fit-content',
    };
});

export default GeneralSection;
